/**
 * O catálogo das páginas que entram no Acesso Rápido -- UM só.
 *
 * ## Por que isto existe
 *
 * Ponto 30 da Gisele (20/10/2026), como ele foi esclarecido: "colocar todas
 * as páginas no quickaccess, pois tem páginas hoje que não estão entrando lá".
 *
 * A causa não era uma página esquecida: eram DUAS listas que tinham de
 * concordar e não concordavam. O `quickAccessConfig` decidia se a página entra
 * por uma expressão regular de caminhos, e o rótulo dela por um mapa separado.
 * Página fora da regex nunca entrava; página na regex mas fora do mapa fazia o
 * `getItem` devolver `null` -- também nunca entrava, e sem erro nenhum.
 *
 * Essa segunda armadilha já tinha mordido uma vez: a tela de WhatsApp estava
 * na regex antes de existir, e quando passou a existir a aba continuou não
 * aparecendo porque ninguém lembrou do mapa.
 *
 * Agora é uma lista. `caminhoEntraNoAcessoRapido` e `paginaDoAcessoRapido` são
 * as duas derivadas dela, então não há como uma saber de uma página que a
 * outra desconhece.
 *
 * ## Como acrescentar uma página
 *
 * Uma linha em `PAGINAS_DO_ACESSO_RAPIDO`. O `id` e a `key` saem do caminho
 * (`/clientes/pesquisa` -> `clientes-pesquisa`), que é exatamente o que as
 * abas já gravadas no `localStorage` usam -- então aba antiga continua
 * abrindo.
 *
 * Se a página não deve virar aba, ela entra em `FORA_DO_ACESSO_RAPIDO`, com o
 * motivo. `App.rotas.test.js` cobra isso: rota nova que não esteja numa das
 * duas listas quebra o teste, em vez de simplesmente nunca aparecer.
 */

/**
 * O `name` é o rótulo da ABA, e aba é estreita: `maxLabelLength` corta em 20
 * caracteres. Por isso ele não copia o texto do menu quando o texto do menu é
 * comprido ("Painel de Atendimentos" virava "Painel de Atendimen…").
 */
export const PAGINAS_DO_ACESSO_RAPIDO = [
    // Atendimento
    { path: '/chat', name: 'Chat' },
    { path: '/painel-atendimentos', name: 'Painel' },
    { path: '/historico', name: 'Histórico' },

    // Tickets
    { path: '/ticket', name: 'Tickets' },
    { path: '/dashboard-pendencias', name: 'Pendências' },
    { path: '/tempo-tickets', name: 'Tempo em tickets' },
    { path: '/filtros', name: 'Filtros' },

    // Clientes
    { path: '/clientes/pesquisa', name: 'Clientes' },
    { path: '/clientes/grupos', name: 'Grupos' },

    // Dia a dia
    { path: '/agenda', name: 'Agenda' },
    { path: '/mural-avisos', name: 'Mural' },

    // Conhecimento
    { path: '/ia-base-conhecimento', name: 'Base de Conhecimento' },
    { path: '/ia-base-conhecimento/videos', name: 'Vídeos' },

    // Canais e administração
    { path: '/whatsapp', name: 'WhatsApp' },
    { path: '/configuracoes', name: 'Configurações' },
    { path: '/operadores', name: 'Operadores' },
    { path: '/logs-atividades', name: 'Logs de Atividades' },
    { path: '/importar-movidesk', name: 'Importar Movidesk' },
    { path: '/importar-clientes', name: 'Importar clientes' },
];

/**
 * As rotas que de propósito NÃO viram aba, e por quê.
 *
 * Existe para o teste de `App.js` poder exigir que toda rota esteja decidida:
 * sem ela, "esqueci de pôr no catálogo" e "decidi deixar fora" ficam
 * indistinguíveis -- que é como as páginas sumiam.
 */
export const FORA_DO_ACESSO_RAPIDO = {
    '/': 'os Indicadores são a tela inicial; fixar a página inicial não acrescenta nada',
    '/indicadores': 'mesma tela da inicial',
    '/orby': 'o Orby tem o seu próprio tipo de aba, que abre o painel lateral em vez de navegar',
    '/perfil': 'o próprio cadastro do operador, visitado de vez em quando pelo menu do avatar',
    '/plataforma': 'só super-admin, e a liberação é por `is_super_admin`, não pela lista de permissões',
    '/teste-notificacoes': 'tela de teste',
    '/novo-operador': 'formulário de criação, não uma página de voltar',
    '/ticket/:id': 'cada chamado já virou aba pelo tipo `tickets`',
    '/ticket/new': 'formulário de criação',
    '/whatsapp/:id': 'a página de um número; a lista (/whatsapp) é a que vira aba',
    '/cliente/:clientId/tickets': 'os chamados de um cliente, alcançados pela ficha dele',
    '/configuracoes/perfil/:profileId': 'a edição de um perfil, dentro de Configurações',
};

/**
 * A permissão de rota que cada página exige, copiada do `requiredPermission`
 * da rota em `App.js`.
 *
 * Caminho sem entrada aqui é liberado -- é o que `canAccessQuickAccessPage`
 * assume. `/chat` é a exceção que não vem da rota: a rota é aberta, mas o
 * Acesso Rápido já exigia `#view-internal-tickets` para oferecer a aba, e
 * mudar isso não é assunto deste ponto.
 */
export const PERMISSAO_DA_PAGINA = {
    '/chat': '#view-internal-tickets',
    '/historico': '/historico',
    '/painel-atendimentos': '/painel-atendimentos',
    '/tempo-tickets': '/tempo-tickets',
    '/clientes/pesquisa': '/clientes/pesquisa',
    '/clientes/grupos': '/clientes/pesquisa',
    '/filtros': '/filtros',
    '/configuracoes': '/configuracoes',
    '/ia-base-conhecimento': '/ia-base-conhecimento',
    // Permissão própria desde 03/11/2026: quem pode os vídeos e não pode a
    // base tem de conseguir guardar a aba dos vídeos no acesso rápido.
    '/ia-base-conhecimento/videos': '/ia-base-conhecimento/videos',
    '/whatsapp': '/whatsapp',
    '/operadores': '/operadores',
    '/logs-atividades': '/logs-atividades',
    '/importar-movidesk': '/importar-movidesk',
    '/importar-clientes': '/importar-clientes',
};

/**
 * O identificador da aba, derivado do caminho.
 *
 * Derivado, e não escrito à mão, porque o `localStorage` do operador guarda
 * abas antigas por `id`: `/clientes/pesquisa` tem de continuar dando
 * `clientes-pesquisa`, senão a aba que ele já tinha deixa de abrir.
 */
export const idDaPagina = (path) => String(path || '').replace(/^\//, '').replace(/\//g, '-');

/** A página do catálogo para um caminho, ou `null` se ela não vira aba. */
export const paginaDoAcessoRapido = (pathname) => {
    const achada = PAGINAS_DO_ACESSO_RAPIDO.find((p) => p.path === pathname);
    if (!achada) return null;

    const id = idDaPagina(achada.path);

    // `key` e `id` sempre iguais: o componente de abas lê um dos dois
    // dependendo do caminho do código, e divergir faria a mesma aba contar
    // como duas.
    return { id, key: id, name: achada.name, path: achada.path };
};

export const caminhoEntraNoAcessoRapido = (pathname) => paginaDoAcessoRapido(pathname) !== null;
